import { activeStatuses, ecId, ecLean, handBack, insertRoll, record, requiredInt, upper, type JanusCtx, type Row, type StatusRow } from '../ability/context.js';
import { abilityChargeEntry, abilityEntryId, ABILITY_CHARGE_BUCKETS } from '../ability/charges/charge-entry.js';
import { adjustCharges, chargeSpendWaivedPools } from '../ability/charges/adjust-charges.js';
import { HOLDING_MASTERY, MASTERY_POOL, touchMasteryMarker, withMasteryMarkerSuppressed } from '../ability/charges/mastery-marker.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';
import { statusItemId, statusPaused, statusRows, statusEndSeconds, statusRemovalBefore, STATUS_IDS } from '../statuses/expiry.js';
import { layerListEntryUuid } from '../../shared/array-types.js';
import { removeStatusConnection } from '../death/compose.js';
import { deathHolders } from '../death/departure.js';
import { deathStatusRows } from '../death/cleanup.js';
import { retireDeathStatus } from '../death/status-removal.js';
import { emptySpawnPlan, mergeSpawnPlans } from '../spawn/row-plan.js';
import { chargeReductionDelta } from '../resources/reduction-guard.js';
import { logPreventedReduction, resourceReductionReaders } from '../ability/resource-reduction.js';

const maintenancePools = (ctx: JanusCtx, definition: Row): string[] =>
  [...new Set(ctx.read.entries(definition, 'maintained_by_ability').map(layerListEntryUuid).filter(Boolean))];

/** Natural expiry retains the native turn flags; a paused source never ages or drains. */
export function maintainedExpiryStatuses(ctx: JanusCtx, holder: Row, boundary: 'start' | 'end'): Row[] {
  const clock = ctx.read.campaignClock();
  const result: Row[] = [];
  const seen = new Set<string>();
  for (const status of [...statusRows(holder, 'caster'), ...statusRows(holder, 'target')]) {
    const id = upper(ctx.read.value(status, 'status_id'));
    if (seen.has(id) || statusPaused(status)) continue;
    seen.add(id);
    const definition = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    if (!definition || !maintenancePools(ctx, definition).length) continue;
    const flag = (key: string): boolean => Number(ctx.read.value(status, key) ?? 0) !== 0;
    const targetStart = flag('status_ends_on_target_start_of_turn'), targetEnd = flag('status_ends_on_target_end_of_turn');
    const casterStart = flag('status_ends_on_caster_start_of_next_turn'), casterEnd = flag('status_ends_on_caster_end_of_next_turn');
    const caster = statusRemovalBefore(status, ecId(holder)).status_caster_id === ecId(holder), target = upper(ctx.read.value(status, 'status_target_id')) === ecId(holder);
    const end = clock ? statusEndSeconds(status, { seconds: ends => {
      for (const unit of ['year', 'month', 'day', 'hour', 'minute', 'second'] as const)
        if (ends[unit] !== clock[unit]) return ends[unit] < clock[unit] ? -1 : 1;
      return 0;
    } }) : null;
    const due = end !== null && end <= 0;
    const expires = boundary === 'start' ? target && targetStart || caster && casterStart
      : target && targetEnd || caster && casterEnd && due;
    if (!expires && !(due && !targetStart && !targetEnd && !casterStart && !casterEnd)) continue;
    result.push(status);
  }
  return result;
}

export function runMaintainedExpiry(ctx: JanusCtx, holder: Row, boundary: 'start' | 'end'): void {
  retireDepartedMaintenance(ctx, holder);
  for (const status of maintainedExpiryStatuses(ctx, holder, boundary)) {
    removeStatusConnection(ctx, status);
    insertRoll(ctx, { roll_type_1: 'status_effect_expired', roll_item_id: statusItemId(status), by: holder });
  }
}

const maintenanceRemoved = (ctx: JanusCtx, status: Row): boolean =>
  ctx.statusesRemoved.some(s => upper(s.status_id) === upper(ctx.read.value(status, 'status_id')))
  || !!ctx.spawnRows?.writes.some(w => w.table === 'statuses' && w.op === 'update' && upper(w.row.status_id) === upper(ctx.read.value(status, 'status_id')));

const maintenanceTargetGone = (ctx: JanusCtx, id: string): boolean => {
  const target = ctx.members.get(id);
  return target ? Number(ecLean(target, 'encounter_creature_deleted') ?? 0) === 1
    : ctx.read.maintainedTargetGone?.(id) === true;
};

/** Only a known summoned maintenance source and a saved departure may end this connection. */
export function departedMaintainedStatuses(ctx: JanusCtx, holder: Row): Row[] {
  return statusRows(holder, 'caster').filter(status => {
    if (statusPaused(status) || maintenanceRemoved(ctx, status)) return false;
    const definition = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    return !!definition && maintenancePools(ctx, definition).length > 0 && ctx.read.entries(definition, 'summon').length > 0
      && maintenanceTargetGone(ctx, upper(ctx.read.value(status, 'status_target_id')));
  });
}

/** Caster upkeep, aggregated by maintaining pool; shared by every native turn boundary. */
export function planMaintainedUpkeep(ctx: JanusCtx, holder: Row): Array<{ id: string; qty: number; current: number; statuses: Row[] }> {
  const costs = new Map<string, { qty: number; statuses: Row[] }>();
  const seen = new Set<string>();
  for (const status of statusRows(holder, 'caster')) {
    if (statusPaused(status) || maintenanceRemoved(ctx, status)) continue;
    const statusId = upper(ctx.read.value(status, 'status_id'));
    if (seen.has(statusId)) continue;
    seen.add(statusId);
    const def = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    if (!def) handBack('maintained:definition-not-held');
    if (ctx.read.entries(def, 'summon').length && maintenanceTargetGone(ctx, upper(ctx.read.value(status, 'status_target_id')))) continue;
    for (const id of maintenancePools(ctx, def)) {
      const cost = costs.get(id) ?? { qty: 0, statuses: [] };
      cost.qty += requiredInt(ctx.read.value(def, 'maintained_by_qty') ?? 1, 'maintained:quantity');
      cost.statuses.push(status); costs.set(id, cost);
    }
  }
  if (!costs.size) return [];
  const waived = chargeSpendWaivedPools(ctx, activeStatuses(ctx, holder));
  return [...costs].filter(([id]) => !waived.has(id)).map(([id, cost]) => ({ id, ...cost,
    current: abilityChargeEntry(ctx, holder, id).current }));
}

function retireDepartedMaintenance(ctx: JanusCtx, holder: Row): void {
  for (const status of departedMaintainedStatuses(ctx, holder)) {
    if (maintenanceRemoved(ctx, status)) continue;
    const holders = deathHolders([ctx.encounter, ...ctx.members.values()]);
    const connection = upper(ctx.read.value(status, 'status_connected_id'));
    // Gone targets have no loss hooks. Retire their leftover tags before native
    // connection removal asks which surviving summons must depart.
    for (const peer of deathStatusRows(holders))
      if (connection && statusItemId(peer) === STATUS_IDS.summon_tag
          && upper(ctx.read.value(peer, 'status_connected_id')) === connection
          && maintenanceTargetGone(ctx, upper(ctx.read.value(peer, 'status_target_id')))) retireDeathStatus(ctx, holders, peer);
    removeStatusConnection(ctx, status);
    // Deleted targets cannot participate in the turn frame. Their exact physical
    // rows use the native saved-status plan, as encounter anchors do.
    const remote = ctx.statusesRemoved.filter(removal => !ctx.members.has(upper(removal.target))
      && connection && upper(ctx.read.value(removal.before ?? null, 'status_connected_id')) === connection && maintenanceTargetGone(ctx, upper(removal.target)));
    if (remote.length) {
      const plan = emptySpawnPlan();
      for (const removal of remote) plan.writes.push({ table: 'statuses', op: 'update',
        row: { status_id: removal.status_id!, status_deleted: 1 }, before: {
          status_deleted: Number(ctx.read.value(removal.before ?? null, 'status_deleted') ?? 0),
          status_target_id: String(ctx.read.value(removal.before ?? null, 'status_target_id') ?? removal.target),
          status_item_id: removal.status_item_id, status_connected_id: String(ctx.read.value(removal.before ?? null, 'status_connected_id') ?? '') } });
      ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
      ctx.statusesRemoved = ctx.statusesRemoved.filter(removal => !remote.includes(removal));
    }
  }
}

export function runMaintainedUpkeep(ctx: JanusCtx, holder: Row): void {
  retireDepartedMaintenance(ctx, holder);
  for (const { id, qty, current, statuses } of planMaintainedUpkeep(ctx, holder)) {
    if (qty > 0 && chargeReductionDelta(holder, -qty, resourceReductionReaders(ctx)) === 0) {
      logPreventedReduction(ctx, holder, 'charges');
      continue;
    }
    adjustCharges(ctx, holder, id, -Math.min(current, qty), true);
    if (current > qty) continue;
    for (const status of statuses) {
      if (maintenanceRemoved(ctx, status)) continue;
      removeStatusConnection(ctx, status);
      insertRoll(ctx, { roll_type_1: 'no_charges_to_maintain', roll_item_id: statusItemId(status), by: holder });
    }
  }
}
import { ftApplyMasteryOutOfCombatTick, ftEnforceMasteryUpkeepAtEndOfTurn } from '../port/k4-dispatch/mastery_upkeep.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import type { E, R } from '../port/k4-dispatch/support-b2.js';
import { nativePortValue } from '../port/native-value.js';
import { heldCharges } from '../reactions/php-row-rules.js';

/** The calendar lane already carries native charge/removal plans; reuse K4's decision. */
export function masteryOutOfCombatDecision(current: number, holding: boolean): { drain: number; removeMarker: boolean } {
  const plan = { drain: 0, removeMarker: false };
  const env = browserDependencies({
    check_uuid: () => true, ft_holding_mastery_status_item_id: () => HOLDING_MASTERY,
    ft_mastery_pool_state: () => ({ id: MASTERY_POOL, current }), has_status_effect: () => holding,
    ft_mastery_marker_suppressed: () => null,
    adjust_charges: (_caster: unknown, _affected: unknown, _id: unknown, delta: number) => { plan.drain = -delta; },
    remove_status_effect_item_id_all_connections_to_target: () => { plan.removeMarker = true; },
  }) as unknown as E;
  nativePortValue(ftApplyMasteryOutOfCombatTick({ value: {} }, { value: [] }, '', null, env));
  return plan;
}

/** Native writes join the initiating turn; the K4 port owns the clock/drain decision. */
export function runMasteryUpkeep(ctx: JanusCtx, holder: Row): void {
  const carried = ABILITY_CHARGE_BUCKETS.flatMap(key => ctx.read.entries(holder, key)).find(entry => abilityEntryId(entry) === MASTERY_POOL);
  const markers = () => activeStatuses(ctx, holder).filter(status => statusItemId(status) === HOLDING_MASTERY);
  if (!carried && !markers().length) return;
  const entry = record(carried);
  if (!markers().length && entry && requiredInt(heldCharges(entry, ctx.read.value), 'k4-upkeep:current-not-held') === 0) return;
  const clock = ctx.read.campaignClock();
  const campaign = clock ? { campaigns: { campaign_id: ecId(holder), ...Object.fromEntries(Object.entries(clock).map(([unit, value]) => ['campaign_time_' + unit, value])) } } : null;
  const row = (value: unknown): Row => { const found = record(value); if (!found) handBack('k4-upkeep:row-not-held'); return found; };
  const deps = {
    check_uuid: (id: unknown) => /^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(String(id)),
    ft_holding_mastery_status_item_id: () => HOLDING_MASTERY,
    ft_mastery_pool_state: () => ({ id: MASTERY_POOL, current: carried ? abilityChargeEntry(ctx, holder, MASTERY_POOL).current : 0 }),
    ft_find_holding_mastery_status_row: () => {
      const held = markers();
      if (held.length > 1) handBack('k4-upkeep:ambiguous-marker');
      const marker = held[0];
      // New/restamped native rows receive their start clock when this commit saves.
      // The upkeep in this same frame must already see that stamp.
      return marker && clock && ctx.statusesAdded.includes(marker as unknown as StatusRow)
        ? { ...marker, ...Object.fromEntries(Object.entries(clock).map(([unit, value]) => ['status_starts_campaign_' + unit, value])) }
        : marker ?? null;
    },
    ft_campaign_context: () => campaign,
    campaign_field: (value: unknown, key: unknown) => record(row(value).campaigns)?.[String(key)] ?? null,
    compare_campaign_times_if_past: (...args: unknown[]) => {
      // Both sides use the same lexicographically ordered campaign tuple.
      for (let i = 0; i < 6; i++) {
        const a = requiredInt(args[i] ?? 0, 'k4-upkeep:clock'), b = requiredInt(args[i + 6] ?? 0, 'k4-upkeep:marker-clock');
        if (a !== b) return a > b;
      }
      return false;
    },
    ft_mastery_marker_suppressed: () => null, // The native scoped latch wraps the entire port below.
    ft_touch_mastery_marker: () => touchMasteryMarker(ctx, holder),
    adjust_charges: (_owner: R, _affected: R, id: unknown, delta: unknown, _roll: unknown, log: unknown) =>
      adjustCharges(ctx, holder, upper(id), requiredInt(delta, 'k4-upkeep:delta'), !!log),
    remove_status_effect_item_id_all_connections_to_target: () => {
      for (const status of markers()) removeStoredStatus(ctx, holder, status);
    },
  };
  const env = browserDependencies(new Proxy(deps, { get: (target, key) => Reflect.get(target, key)
    ?? (() => handBack('k4-upkeep:dependency:' + String(key))) })) as unknown as E;
  // A missing marker is stamped normally; only an actual drain suppresses restamping.
  const run = () => nativePortValue(ftEnforceMasteryUpkeepAtEndOfTurn({ value: holder }, { value: [] }, '', ctx.encounter, env));
  if (markers().length) withMasteryMarkerSuppressed(ctx, run); else run();
}
